import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Alert,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Card,
} from '@mui/material';
import { SaveAlt as ExportIcon } from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import dayjs from 'dayjs';

function convertToCSV(data, regionMap) {
  const header = [
    'S.No', 'Company Name', 'User Type', 'Contact Person', 'Mobile', 'Alternate Number',
    'Email','Region', 'Street Line', 'Town/City', 'State', 'Country', 'Pincode', 'GSTIN', 'PAN',
  ];
  const rows = data.map((user, idx) => {
    const regionIds = user.region_id ? String(user.region_id).split(',').map(id => id.trim()) : [];
    return [
      idx + 1,
      user.company_name ?? '',
      user.user_type?.name || user.user_type || '',
      user.contact_person ?? '',
      user.mobile ?? '',
      user.alternate_number ?? '',
      user.email ?? '',
      user.street_line ?? '',
      user.town_city ?? '',
      user.state ?? '',
      user.country ?? '',
      user.pincode ?? '',
      user.gstin ?? '',
      user.pan ?? '',
      regionIds.length > 0
        ? regionIds.map(id => regionMap[id] ?? 'Unknown').join(', ')
        : 'N/A',
    ];
  });
  return [header, ...rows]
    .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

const AppUserReport = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [regionMap, setRegionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedUserType, setSelectedUserType] = useState('');
  const [userTypes, setUserTypes] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportSuccess, setExportSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, regionRes] = await Promise.all([
          axios.get(API_ENDPOINTS.APP_USERS.BASE),
          axios.get(API_ENDPOINTS.REGION_NAMES),
        ]);
        const userData = Array.isArray(userRes.data.appUsers)
          ? userRes.data.appUsers
          : Array.isArray(userRes.data)
          ? userRes.data
          : [];
        const processedUsers = userData.map(user => ({
          ...user,
          region_id: user.region_id ? String(user.region_id).split(',').map(id => id.trim()) : [],
        }));
        const userTypes = [...new Set(userData
          .map(user => user.user_type?.name || user.user_type)
          .filter(Boolean))];
        setUserTypes(userTypes);
        const map = {};
        (Array.isArray(regionRes.data) ? regionRes.data : []).forEach((r) => {
          map[r.id] = r.name;
        });
        setUsers(processedUsers);
        setFilteredUsers(processedUsers);
        setRegionMap(map);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || 'Failed to fetch user or region data.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = users;
    if (fromDate) {
      filtered = filtered.filter((user) => {
        const date = user.created_at || user.registration_date;
        return date && dayjs(date).isAfter(dayjs(fromDate).subtract(1, 'day'));
      });
    }
    if (toDate) {
      filtered = filtered.filter((user) => {
        const date = user.created_at || user.registration_date;
        return date && dayjs(date).isBefore(dayjs(toDate).add(1, 'day'));
      });
    }
    if (selectedUserType) {
      filtered = filtered.filter((user) => 
        (user.user_type?.name || user.user_type || '') === selectedUserType
      );
    }
    if (selectedRegion.length > 0 && !selectedRegion.includes('all')) {
      filtered = filtered.filter((user) => 
        selectedRegion.some(regionId => user.region_id.includes(regionId))
      );
    }
    filtered = filtered.filter(
      (user) =>
        (user.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.contact_person?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.mobile?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.alternate_number?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users, fromDate, toDate, selectedUserType, selectedRegion]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sorted = [...filteredUsers].sort((a, b) => {
      if (key === 'region_id') {
        const aRegion = a.region_id.length > 0
          ? a.region_id.map(id => regionMap[id] ?? 'Unknown').join(', ')
          : 'N/A';
        const bRegion = b.region_id.length > 0
          ? b.region_id.map(id => regionMap[id] ?? 'Unknown').join(', ')
          : 'N/A';
        return direction === 'asc'
          ? aRegion.localeCompare(bRegion)
          : bRegion.localeCompare(aRegion);
      }
      const aValue = key === 'user_type' ? (a.user_type?.name || a.user_type || '') : (a[key] ?? '');
      const bValue = key === 'user_type' ? (b.user_type?.name || b.user_type || '') : (b[key] ?? '');
      return direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    setFilteredUsers(sorted);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    try {
      const paginatedUsers =
        rowsPerPage > 0
          ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredUsers;
      const csv = convertToCSV(paginatedUsers, regionMap);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `app_users_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      setError('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box component="section" sx={{ mt: 6 }}>
      <Card sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {exportSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully exported {filteredUsers.length} records to CSV
          </Alert>
        )}

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" m={3}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>User Type</InputLabel>
            <Select
              value={selectedUserType}
              onChange={(e) => setSelectedUserType(e.target.value)}
              label="User Type"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {userTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Region</InputLabel>
            <Select
              multiple
              value={selectedRegion}
              onChange={(e) => {
                const value = e.target.value;
                // If "all" is selected, clear other selections
                if (value.includes('all')) {
                  setSelectedRegion(['all']);
                } else {
                  setSelectedRegion(value);
                }
              }}
              label="Region"
              renderValue={(selected) => {
                if (selected.includes('all')) {
                  return 'All Regions';
                }
                return selected.length === 0 
                  ? 'All'
                  : selected.map(id => regionMap[id] ?? 'Unknown').join(', ');
              }}
            >
              <MenuItem value="all">
                <em>All Regions</em>
              </MenuItem>
              {Object.entries(regionMap).map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search by company, contact, or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            sx={{ maxWidth: 400 }}
          />
          <Box sx={{ ml: 'auto' }}>
            <Button 
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExportCSV}
              disabled={users.length === 0}
            >
              Export CSV ({filteredUsers.length} records)
            </Button>
          </Box>
          {(fromDate || toDate || searchTerm || selectedUserType || selectedRegion.length > 0) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setSearchTerm('');
                setSelectedUserType('');
                setSelectedRegion([]);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {filteredUsers.length === 0 ? (
          <Alert severity="info">No users found.</Alert>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2' }}>
                    {[
                      'S.No', 'Company Name', 'User Type', 'Contact Person', 'Mobile', 'Alternate Number',
                      'Email', 'Region', 'Street Line', 'Town/City', 'State', 'Country', 'Pincode', 'GSTIN', 'PAN',
                    ].map((header, idx) => {
                      const key = header === 'Region' ? 'region_id' : header.toLowerCase().replace(/ /g, '_');
                      return (
                        <TableCell
                          key={key}
                          sortDirection={sortConfig.key === key ? sortConfig.direction : false}
                          sx={{
                            backgroundColor: '#1976d2',
                            color: '#FFF',
                            fontWeight: 'bold',
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.key === key}
                            direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                            onClick={() => key !== 'sno' && handleSort(key)}
                            sx={{ color: 'inherit' }}
                            hideSortIcon={header === 'S.No'}
                          >
                            {header}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user, idx) => (
                      <TableRow key={user.id} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{user.company_name ?? 'N/A'}</TableCell>
                        <TableCell>{user.user_type?.name || user.user_type || 'N/A'}</TableCell>
                        <TableCell>{user.contact_person ?? 'N/A'}</TableCell>
                        <TableCell>{user.mobile ?? 'N/A'}</TableCell>
                        <TableCell>{user.alternate_number ?? 'N/A'}</TableCell>
                        <TableCell>{user.email ?? 'N/A'}</TableCell>
                        <TableCell>
                          {user.region_id.length > 0
                            ? user.region_id.map(id => regionMap[id] ?? 'Unknown').join(', ')
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{user.street_line ?? 'N/A'}</TableCell>
                        <TableCell>{user.town_city ?? 'N/A'}</TableCell>
                        <TableCell>{user.state ?? 'N/A'}</TableCell>
                        <TableCell>{user.country ?? 'N/A'}</TableCell>
                        <TableCell>{user.pincode ?? 'N/A'}</TableCell>
                        <TableCell>{user.gstin ?? 'N/A'}</TableCell>
                        <TableCell>{user.pan ?? 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={15}
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                border: "none",
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AppUserReport;